/** Create / edit dialog for a block list (react-hook-form + zod for the
 *  scalar fields, plus a local-state ordered-items editor).
 *
 *  A block list is an ordered, nestable grouping. Each row is EITHER a
 *  block OR a nested list (a "Block / List" toggle picks which), with an
 *  optional `alias` overriding the name it appears under. Rows reorder with the
 *  up/down buttons and the array order is submitted as each item's `order`.
 *  `items` is reconciled wholesale on write. `tenant` is clearable: cleared =
 *  a system-wide list (`tour_operator: null`); the scope is also passed to the
 *  item pickers so they search the right operator's catalog. */
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as blocksApi from '@/api/notificationBlocks';
import {
  create as createList,
  update as updateList,
  type BlockListDetail,
  type BlockListRef,
  type BlockListWrite,
  type BlockListWriteItem,
  type BlockRef,
} from '@/api/blockLists';
import * as blockListsApi from '@/api/blockLists';
import { AppDialog } from '@/components/AppDialog';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';
import { singlePageTenant } from '@/scope/pageTenants';

type ItemKind = 'block' | 'list';

/** One editor row (local state) — exactly one of block / blockList is
 *  set once picked. `key` is a stable React key for reorder/remove. */
interface EditorRow {
  key: string;
  kind: ItemKind;
  block: BlockRef | null;
  blockList: BlockListRef | null;
  alias: string;
}

let keySeq = 0;
const nextKey = () => `row-${keySeq++}`;

function toRows(detail: BlockListDetail | null): EditorRow[] {
  if (!detail) return [];
  return detail.items.map((it) => ({
    key: nextKey(),
    kind: it.block ? 'block' : 'list',
    block: it.block,
    blockList: it.block_list,
    alias: it.alias ?? '',
  }));
}

const FormSchema = z.object({
  codename: z
    .string()
    .trim()
    .min(1, 'Codename is required')
    .max(128)
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      'Letters, digits and underscore only; cannot start with a digit.',
    ),
  name: z.string().max(256),
  description: z.string(),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = { codename: '', name: '', description: '', enabled: true };

function toDefaults(list: BlockListDetail | null): FormValues {
  if (!list) return EMPTY;
  return {
    codename: list.codename,
    name: list.name ?? '',
    description: list.description ?? '',
    enabled: list.enabled,
  };
}

const optionLabel = (o: { codename: string; name?: string }) =>
  o.name ? `${o.codename} — ${o.name}` : o.codename;

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  list: BlockListDetail | null;
  /** The page's operators (D9), for create: the one is pre-selected, all are
   *  featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function BlockListFormDialog({ open, list, pageTenants, onClose }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = list !== null;
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.block_list',
    objectPk: list?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['block_lists'] }),
  });

  // Tenant + ordered items live outside RHF (self-contained controlled inputs).
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [rows, setRows] = useState<EditorRow[]>([]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(list),
  });

  useEffect(() => {
    if (!open) return;
    history.reset();
    reset(toDefaults(list));
    setRows(toRows(list));
    if (list) {
      setTenant(
        list.tenant
          ? { id: list.tenant.id, name: list.tenant.name, code: list.tenant.code }
          : null,
      );
    } else {
      setTenant(singlePageTenant(pageTenants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, list?.id]);

  const tenantId = tenant?.id ?? null;
  const editingId = list?.id ?? null;

  const patchRow = (key: string, patch: Partial<EditorRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const setKind = (key: string, kind: ItemKind) =>
    patchRow(key, { kind, block: null, blockList: null });
  const addRow = () =>
    setRows((rs) => [
      ...rs,
      { key: nextKey(), kind: 'block', block: null, blockList: null, alias: '' },
    ]);
  const removeRow = (key: string) => setRows((rs) => rs.filter((r) => r.key !== key));
  const moveRow = (index: number, dir: -1 | 1) =>
    setRows((rs) => {
      const target = index + dir;
      if (target < 0 || target >= rs.length) return rs;
      const next = [...rs];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });

  const mutation = useMutation({
    mutationFn: (body: BlockListWrite) =>
      isEdit ? updateList(list.id, body) : createList(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Block list updated.' : 'Block list created.', 'success');
      qc.invalidateQueries({ queryKey: ['block_lists'] });
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    // Ordered array; drop unfilled rows. Exactly one id per item.
    const items: BlockListWriteItem[] = [];
    rows.forEach((r, i) => {
      if (r.kind === 'block' && r.block) {
        items.push({ block_id: r.block.id, alias: r.alias.trim() || null, order: i });
      } else if (r.kind === 'list' && r.blockList) {
        items.push({ block_list_id: r.blockList.id, alias: r.alias.trim() || null, order: i });
      }
    });
    mutation.mutate({
      tour_operator: tenantId,
      codename: v.codename.trim(),
      name: v.name.trim(),
      description: v.description.trim(),
      enabled: v.enabled,
      items,
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={isEdit ? `Edit block list · ${list.codename}` : 'New block list'}
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create list'}
            </Button>
          </>
        )
      }
    >
      {history.header}
      {history.historyPane ?? (
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            {...register('codename')}
            label="Codename"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.codename)}
            helperText={errors.codename?.message ?? 'Identifier used to reference this list.'}
          />
          <TextField
            {...register('name')}
            label="Name"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.name)}
            helperText={errors.name?.message ?? 'Optional human-readable label.'}
          />
        </Box>

        <TextField
          {...register('description')}
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          error={Boolean(errors.description)}
          helperText={errors.description?.message ?? 'Optional.'}
        />

        <TenantPicker
          value={tenant}
          featured={isEdit ? undefined : pageTenants}
          onChange={setTenant}
          label="Operator"
          placeholder="System / global (no operator)"
        />

        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label="Enabled"
            />
          )}
        />

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            Items
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Ordered members of this list — each a block or a nested list, with an optional alias
            override. Order is preserved on save.
          </Typography>

          <Stack spacing={1.5}>
            {rows.map((row, i) => (
              <ItemRow
                key={row.key}
                row={row}
                index={i}
                total={rows.length}
                tenantId={tenantId}
                editingId={editingId}
                onPatch={(patch) => patchRow(row.key, patch)}
                onKind={(kind) => setKind(row.key, kind)}
                onMove={(dir) => moveRow(i, dir)}
                onRemove={() => removeRow(row.key)}
              />
            ))}
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No items yet.
              </Typography>
            )}
          </Stack>

          <Button startIcon={<AddIcon />} size="small" sx={{ mt: 1.5 }} onClick={addRow}>
            Add item
          </Button>
        </Box>
      </Box>
      )}
    </AppDialog>
  );
}

interface ItemRowProps {
  row: EditorRow;
  index: number;
  total: number;
  tenantId: number | null;
  editingId: number | null;
  onPatch: (patch: Partial<EditorRow>) => void;
  onKind: (kind: ItemKind) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

function ItemRow({
  row,
  index,
  total,
  tenantId,
  editingId,
  onPatch,
  onKind,
  onMove,
  onRemove,
}: ItemRowProps) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={row.kind}
          onChange={(_, v: ItemKind | null) => v && onKind(v)}
          aria-label="Item kind"
          sx={{ mt: 0.25 }}
        >
          <ToggleButton value="block">Block</ToggleButton>
          <ToggleButton value="list">List</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1, minWidth: 240 }}>
          {row.kind === 'block' ? (
            <RemoteAutocomplete<BlockRef>
              label="Block"
              size="small"
              fetchOnEmpty
              value={row.block}
              getOptionLabel={optionLabel}
              getOptionKey={(o) => o.id}
              queryFn={async (q) => {
                const r = await blocksApi.list({
                  search: q,
                  pageSize: 20,
                  ...(tenantId != null ? { tour_operator_id: tenantId } : {}),
                });
                return r.results.map((b) => ({
                  id: b.id,
                  codename: b.codename,
                  name: b.name,
                  type: b.type,
                }));
              }}
              onChange={(v) => onPatch({ block: v as BlockRef | null })}
            />
          ) : (
            <RemoteAutocomplete<BlockListRef>
              label="List"
              size="small"
              fetchOnEmpty
              value={row.blockList}
              getOptionLabel={optionLabel}
              getOptionKey={(o) => o.id}
              queryFn={async (q) => {
                const r = await blockListsApi.list({
                  search: q,
                  pageSize: 20,
                  ...(tenantId != null ? { tour_operator_id: tenantId } : {}),
                });
                return r.results
                  // A list can never contain itself (the backend rejects cycles).
                  .filter((l) => l.id !== editingId)
                  .map((l) => ({ id: l.id, codename: l.codename, name: l.name }));
              }}
              onChange={(v) => onPatch({ blockList: v as BlockListRef | null })}
            />
          )}
        </Box>

        <TextField
          label="Alias"
          size="small"
          value={row.alias}
          onChange={(e) => onPatch({ alias: e.target.value })}
          sx={{ width: 160 }}
          helperText="Optional"
        />

        <Box sx={{ display: 'flex', mt: 0.25 }}>
          <IconButton
            aria-label="Move up"
            size="small"
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="Move down"
            size="small"
            disabled={index === total - 1}
            onClick={() => onMove(1)}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Remove item" size="small" onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
